import {
  Block,
  Blocks,
  cloneBlock,
  BlockList,
} from '@chameleoncreator/creator-app';
import getBlockById from 'services/apiHelpers/getBlockById';
import uuid from 'uuidv4';
import { findBlocksIn } from 'selectors/block';
import getAllInteractionsOfType from 'services/apiHelpers/getAllInteractionsOfType';
import getTopics from 'services/apiHelpers/getTopics';
import getAllInteractions from 'services/apiHelpers/getAllInteractions';
import getStore from 'services/getStore';

export const flatten = (block: Block): [Blocks, string] => {
  let blocks: Blocks = {};
  const rootBlockId = block.id;
  const rootBlock = cloneBlock(block);
  blocks[rootBlockId] = rootBlock;

  if (Array.isArray(rootBlock.children)) {
    rootBlock.children.forEach(child => {
      if (child.type === 'Ref') {
        return;
      }

      const [childBlocks] = flatten(child);
      blocks = {
        ...blocks,
        ...childBlocks,
      };
    });
  }

  makeReference(blocks);

  return [blocks, rootBlockId];
};

export const makeReference = (blocks: Blocks) => {
  Object.keys(blocks).forEach(k => {
    const children = blocks[k].children;
    if (Array.isArray(children)) {
      let c = children.map(child => ({ id: child.id, type: 'Ref' })) as Block[];
      blocks[k].children = c;
    }
  });
};

// TODO: Remove this function it is DANGEROUS because block is a JS REFERENCE POINTER
export const expand = (blocks: Blocks, block: Block): Block => {
  let b;

  if (block.type === 'Ref') {
    const fetchedBlock = blocks[block.id];
    b = cloneBlock(fetchedBlock);
  } else {
    b = cloneBlock(block);
  }
  if (b.children && Array.isArray(b.children)) {
    b.children = b.children.map(c => expand(blocks, c));
  }

  return b;
};

export const expandBlock = (blocks: Blocks) => (block: Block): Block => {
  return expand(blocks, block);
};

// Using the known interactions, create a duplicated interactions name.
export const makeInteractionName = (
  type: string,
  dupCtx: duplicationCtx
): string => {
  const iMap = {
    TextAndImage: 'Media',
    DragAndDrop: 'Matching',
    AssessmentQuestion: 'Question',
  };

  const typeName = iMap[type] || type;

  const prevDuplicationOfType = dupCtx.interactionDuplications[type] || 0;
  const totalTypeCount =
    getAllInteractionsOfType(type).length + prevDuplicationOfType;

  return `${typeName} #${totalTypeCount + 1}`;
};

// TODO: Change duplicate block to use block provided duplicate function
// Take a single block and return a new block with all the appropriate data updated correctly
export const duplicateBlock = (
  block: Block,
  dupCtx: duplicationCtx,
  index?: number
): Block => {
  // Clone Block
  const nextBlock = cloneBlock(block);

  // Reset ID (uuidv4())
  nextBlock.id = uuid();

  // Update relevant data within a block
  switch (block.type) {
    case 'Topic':
      //
      const topics = getTopics();
      let name;

      if (block.props?.asAssessment) {
        name = `Assessment #${topics.filter((t: Block) => t.props?.asAssessment)
          .length + 1}`;
      } else {
        name = `Topic #${topics.filter((t: Block) => !t.props?.asAssessment)
          .length + 1}`;
      }

      nextBlock.props!.name = name;

      // update with new sId
      nextBlock.props!.sId = getSId('topic'); // uuid();

      break;

    case 'Interaction':
      nextBlock.props!.name = makeInteractionName(
        nextBlock.meta!.subType,
        dupCtx
      );

      // update with new sId
      const nextSId = getSId('interaction');
      // console.log(nextSId, index, nextSId + index);
      nextBlock.props!.sId =
        index === undefined ? nextSId : nextSId + (index - 1); // uuid();

      dupCtx.interactionDuplications[block.meta!.subType] =
        (dupCtx.interactionDuplications[block.meta!.subType] || 0) + 1;
      break;
    // By default do nothing
    default:
  }

  // console.log(dupCtx);

  // Return new block
  return nextBlock;
};

interface duplicationCtx {
  interactionDuplications: { [key: string]: number };
}

// Give a root block and all the blocks that we know about, duplicate a tree of blocks and return them
// see selectors/block.ts for function findBlocksIn() using identity selector
export const duplicateBlocks = (
  root: Block,
  blocks: Blocks
): [Blocks, string] => {
  // Create a duplication context. This is an object that keeps information relevant to this call
  // to duplicate. It is like temporary shared state, but shared by an object reference
  const dupCtx: duplicationCtx = {
    interactionDuplications: {},
  };

  // Find a list of all the blocks that use the provided root
  const list: Block[] = findBlocksIn(blocks, root, () => true);

  // create map for old to new ids
  const idMap: { [key: string]: string } = {};

  // Loop through all the blocks and duplicate them
  const nextBlocks: BlockList = [];
  list.forEach((b, index) => {
    const nextBlock = duplicateBlock(b, dupCtx, index);
    nextBlocks.push(nextBlock);
    idMap[b.id] = nextBlock.id;
  });

  // Loop through all the blocks a second time to update child refs
  const finalBlocksList = nextBlocks.map(child => {
    if (Array.isArray(child.children)) {
      child.children = child.children.map(ref => {
        return {
          type: 'Ref',
          id: idMap[ref.id],
        };
      });
    }
    return child;
  });

  // Update reference ID's
  const finalBlocks = finalBlocksList.reduce((accu: Blocks, curr: Block) => {
    accu[curr.id] = curr;
    return accu;
  }, {} as Blocks);

  // Duplicate provided root block
  const newRoot = duplicateBlock(root, dupCtx);
  if (Array.isArray(newRoot.children)) {
    newRoot.children = newRoot.children.map(ref => {
      return {
        type: 'Ref',
        id: idMap[ref.id],
      };
    });
  }

  // Add the new root to the object of
  finalBlocks[newRoot.id] = newRoot;

  // Return the new blocks
  return [finalBlocks, newRoot.id];
};

export const getSId = (type, offset: number = 0) => {
  const list =
    type === 'topic'
      ? getTopics()
      : getAllInteractions()
        .map(i => getBlockById(i))
        .filter(i => i);

  // console.log({ type, list });

  let SIds = list.map((entry: any) => entry.props.sId);

  if (!SIds.length) {
    SIds = [0];
  }

  const nextSId = Math.max(...SIds) + 1 + offset;

  // console.log({ type, nextSId });

  return nextSId;
};
